import { Product } from '../types/CartItem';
import { ProductApi, ProductListApiResponse } from '../types/productTypes';

const API_BASE = process.env.REACT_APP_IMS_API_BASE_URL || 'http://localhost:4001';
const PRODUCTS_PATH = process.env.REACT_APP_PRODUCTS_PATH || '/api/items';
const DEFAULT_PAGE_SIZE = 200;
const token = process.env.REACT_APP_IMS_API_KEY || '';
function toNumberOrNull(s: string | null | undefined): number | null {
  if (s == null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize value like "9.59E+12" or "012630536" into a clean numeric string without separators.
 * If it's already digits, returns as-is.
 */
export function normalizeBarcodeLike(value: string | null | undefined): string | null {
  if (!value) return null;

  // Fast-path: already digits only.
  if (/^\d+$/.test(value)) return value;

  // Try number parse (handles scientific notation safely for UPC/EAN lengths)
  const n = Number(value);
  if (Number.isFinite(n)) {
    // UPC/EAN rarely > 14 digits; still well below Number.MAX_SAFE_INTEGER (9e15)
    return Math.trunc(n).toString();
  }

  // Last resort: strip non-digits (won't expand scientific notation correctly but avoids crashes)
  const digits = value.replace(/\D+/g, '');
  return digits || null;
}

export function mapApiToProduct(p: ProductApi): Product {
  const name =
    p.product_display_name?.trim() ||
    p.product_name?.trim() ||
    'Unnamed Product';

  const sku = (p.item_sku ?? '').trim() || null;
  const upc = normalizeBarcodeLike(p.item_upc);
  const barcode = sku ?? upc ?? '';

  const retail = toNumberOrNull(p.retail_price) ?? 0;

  const imageUrl = Array.isArray(p.image) && p.image.length > 0 ? p.image[0] : undefined;

  const category =
    p.sub_category_data?.sub_category_description_en ??
    p.category_data?.category_description_en ??
    'General';

  const description = p.english_description ?? p.french_description ?? '';
  const qty = p.quantity ?? 0;

  const bottleDeposit = toNumberOrNull(p.bottle_deposit) ?? undefined;
  const bottleDepositApplicable = Boolean(p.bottle_deposit_applicable);

  const taxPct = toNumberOrNull(p.tax_data?.tax_percentage ?? null);
  const taxRate = typeof taxPct === 'number' ? taxPct / 100 : undefined;

  return {
    id: String(p.item_id),
    name,
    price: retail,
    barcode,
    category,
    description,
    imageUrl,
    isbn: upc ?? undefined,
    author: undefined,
    publisher: p.vendor ?? undefined,
    inStock: qty > 0,
    stockQuantity: qty,

    taxApplicable: Boolean(p.tax_applicable),
    taxRate,
    taxCode: p.tax_data?.tax_code ?? undefined,

    bottleDeposit,
    bottleDepositApplicable,
  };
}

class ProductService {
  private products: Product[] = [];
  private loadedAt: number | null = null;
  private loadingPromise: Promise<Product[]> | null = null;

  /** Load full product catalog (auto-paginates if backend supports page/pageSize/totalCount) */
  async loadAll(force = false): Promise<Product[]> {
    console.log("Loading products from API...");
    if (!force && this.products.length && this.loadedAt && Date.now() - this.loadedAt < 5 * 60 * 1000) {
      return this.products; // simple 5-min cache
    }
    if (this.loadingPromise) return this.loadingPromise;

    let headers = { 'Authorization': `Bearer ${token}` };

    this.loadingPromise = (async () => {
      const firstUrl = `${API_BASE}${PRODUCTS_PATH}?page=1&pageSize=${DEFAULT_PAGE_SIZE}`;
      const all: Product[] = [];

      let url = firstUrl;
      let page = 1;
      let total = Infinity;

      while (true) {
        const res = await fetch(url, { method: 'GET', headers  });
        if (!res.ok) throw new Error(`Products fetch failed: ${res.status}`);
        const json = (await res.json()) as ProductListApiResponse;

        const batch = (json.data ?? []).map(mapApiToProduct);
        all.push(...batch);

        const pageSize = json.pagination?.pageSize ?? batch.length;
        const totalCount = json.pagination?.totalCount ?? batch.length;
        total = totalCount;

        const fetchedSoFar = (page) * pageSize;
        if (fetchedSoFar >= total || batch.length === 0 || pageSize === 0) break;

        page += 1;
        url = `${API_BASE}${PRODUCTS_PATH}?page=${page}&pageSize=${pageSize}`;
      }

      this.products = all;
      this.loadedAt = Date.now();
      this.loadingPromise = null;
      return all;
    })();

    return this.loadingPromise;
  }

  /** Try to find a product by any code (UPC/ISBN-like/SKU). */
  async findByCode(input: string): Promise<Product | null> {
    console.log('Searching for product by code:', input);
    await this.loadAll();
    const original = input.trim();
    const digits = normalizeBarcodeLike(original);

    // Match rules:
    // - exact barcode
    // - exact isbn (we store UPC in isbn for compatibility)
    // - exact original input (for legacy codes)
    return (
      this.products.find(p =>
        (p.barcode && p.barcode === (digits ?? original)) ||
        (p.isbn && p.isbn === (digits ?? original)) ||
        p.barcode === original
      ) ?? null
    );
  }

  /** Expose list for UI that wants to preload/show all products */
  async getAll(): Promise<Product[]> {
    return this.loadAll();
  }

  clearCache() {
    this.products = [];
    this.loadedAt = null;
    this.loadingPromise = null;
  }
}

export const productService = new ProductService();
